import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, X, Send, ChevronUp, User, Mail, Globe, Info } from 'lucide-react';

// Enhanced Chat Hook with auto-scroll, line breaks, and auto-open - FIXED VERSION
const useImprovedChat = (clerkUser) => {
  const [isChatOpen, setIsChatOpen] = useState(() => sessionStorage.getItem('chat-open') === 'true');
  const [chatClosing, setChatClosing] = useState(false);
  const [userClosed, setUserClosed] = useState(() => sessionStorage.getItem('chat-closed') === 'true');
  const seenMessageIds = useRef(new Set());
  const [chatStage, setChatStage] = useState('name');
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');

  // Load session ID from localStorage — keyed by Clerk user ID if logged in
  const getStorageKey = (user) => user?.id ? `sc-chat-${user.id}` : 'sc-chat-guest';
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem(getStorageKey(clerkUser)) || null; } catch { return null; }
  });
  const [isLoading, setIsLoading] = useState(false);
  
  // Use refs to avoid stale closures in polling
  const messagesRef = useRef([]);
  const sessionIdRef = useRef(null);
  const chatStageRef = useRef('name');
  const messageInputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const isChatOpenRef = useRef(false);

  // Make chat toggle function available globally
  useEffect(() => {
    window.openLiveChat = () => {
      setIsChatOpen(true);
    };
    
    // Cleanup
    return () => {
      delete window.openLiveChat;
    };
  }, []);

  // Update refs when state changes
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    chatStageRef.current = chatStage;
  }, [chatStage]);

  useEffect(() => {
    isChatOpenRef.current = isChatOpen;
  }, [isChatOpen]);

  // Auto-scroll to bottom when NEW messages arrive (smooth scroll for new messages only)
  useEffect(() => {
    if (messagesContainerRef.current && isChatOpen) {
      const container = messagesContainerRef.current;
      
      // Enable smooth scrolling for new messages
      container.style.scrollBehavior = 'smooth';
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  // FIXED: Instantly jump to bottom when chat opens (no animation)
  useEffect(() => {
    if (isChatOpen && messagesContainerRef.current && messages.length > 0) {
      // Disable smooth scrolling temporarily
      const container = messagesContainerRef.current;
      const originalBehavior = container.style.scrollBehavior;
      container.style.scrollBehavior = 'auto';
      
      // Instantly jump to bottom
      container.scrollTop = container.scrollHeight;
      
      // Restore smooth scrolling for future use
      setTimeout(() => {
        container.style.scrollBehavior = originalBehavior;
      }, 50);
    }
  }, [isChatOpen]);

  // Skip name/email stages if user is already logged in via Clerk
  useEffect(() => {
    if (clerkUser && chatStage !== 'chat') {
      const name = clerkUser.firstName
        ? `${clerkUser.firstName}${clerkUser.lastName ? ' ' + clerkUser.lastName : ''}`.trim()
        : clerkUser.primaryEmailAddress?.emailAddress || '';
      const email = clerkUser.primaryEmailAddress?.emailAddress || '';
      setUserName(name);
      setUserEmail(email);
      setChatStage('chat');
      try {
        const existingSession = localStorage.getItem(getStorageKey(clerkUser));
        if (existingSession) setSessionId(existingSession);
      } catch {}
    }
  }, [clerkUser]);

  // Fetch chat history from server for logged-in users — syncs across devices
  useEffect(() => {
    if (!clerkUser?.id) return;
    const fetchHistory = async () => {
      try {
        const res = await fetch('/api/chat-history.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ clerk_user_id: clerkUser.id, session_id: sessionId })
        });
        const data = await res.json();
        if (data.success && data.messages?.length > 0) {
          if (data.session_id) setSessionId(data.session_id);
          const restored = data.messages.map((m, i) => ({
            id: `${m.created_at}-${m.message}`,
            text: m.message,
            sender: m.sender,
            timestamp: new Date(m.created_at)
          }));
          // Mark all loaded messages as already seen so polling doesn't re-trigger auto-open
          restored.forEach(m => seenMessageIds.current.add(m.id));
          setMessages(restored);
        }
      } catch (e) {
        console.error('Failed to fetch chat history:', e);
      }
    };
    fetchHistory();
  }, [clerkUser?.id]);

  // Persist sessionId to localStorage whenever it changes
  useEffect(() => {
    if (sessionId) {
      try { localStorage.setItem(getStorageKey(clerkUser), sessionId); } catch {}
    }
  }, [sessionId]);

  // Auto-focus message input when chat starts
  useEffect(() => {
    if (chatStage === 'chat' && messageInputRef.current) {
      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 100);
    }
  }, [chatStage]);

  // FIXED: Load cached chat data on component mount - skip for Clerk users (DB is source of truth)
  useEffect(() => {
    if (clerkUser) return; // Clerk users load from DB via fetchHistory
    try {
      const cachedData = localStorage.getItem('spiral-chat-session');
      if (cachedData) {
        const data = JSON.parse(cachedData);
        const hasUserMessages = data.messages && data.messages.some(msg => msg.sender === 'user');
        if (data.sessionId && data.userName && data.userEmail && hasUserMessages) {
          console.log('Loading cached chat session with user messages');
          setSessionId(data.sessionId);
          setUserName(data.userName);
          setUserEmail(data.userEmail);
          // Mark cached messages as seen
          data.messages.forEach(m => seenMessageIds.current.add(m.id));
          setMessages(data.messages);
          setChatStage('chat');
        } else {
          localStorage.removeItem('spiral-chat-session');
        }
      }
    } catch (error) {
      console.error('Failed to load cached chat:', error);
      localStorage.removeItem('spiral-chat-session');
    }
  }, []);

  // Enhanced polling logic with auto-open feature and improved deduplication
  useEffect(() => {
    let pollInterval;
    
    const startPolling = () => {
      pollInterval = setInterval(async () => {
        const currentSessionId = sessionIdRef.current;
        const currentChatStage = chatStageRef.current;
        
        if (!currentSessionId || currentChatStage !== 'chat') {
          return;
        }

        try {
          const response = await fetch(`/api/chat-messages.php?session_id=${currentSessionId}`);
          
          if (!response.ok) {
            console.error('Failed to poll messages:', response.status, response.statusText);
            return;
          }
          
          const result = await response.json();
          
          if (result.success && result.messages) {
            const currentMessages = messagesRef.current;
            
            // Get all new support messages
            const existingMessageIds = new Set(
              currentMessages
                .filter(msg => msg.sender === 'support')
                .map(msg => msg.id)
            );
            
            // Filter new support messages by unique message ID/timestamp, not content
            const newSupportMessages = result.messages
              .filter(msg => msg.sender === 'support')
              .map(msg => ({
                id: `${msg.created_at}-${msg.message}`, // Unique ID based on timestamp + content
                text: msg.message,
                sender: 'support',
                timestamp: new Date(msg.created_at)
              }))
              .filter(msg => !existingMessageIds.has(msg.id));
            
            if (newSupportMessages.length > 0) {
              console.log('Adding new support messages:', newSupportMessages);
              
              // Only auto-open for messages we haven't seen before (not on page reload)
              const trulyNewMessages = newSupportMessages.filter(msg => !seenMessageIds.current.has(msg.id));
              newSupportMessages.forEach(msg => seenMessageIds.current.add(msg.id));

              if (trulyNewMessages.length > 0) {
                const currentIsChatOpen = isChatOpenRef.current;
                if (!currentIsChatOpen) {
                  console.log('Auto-opening chat for new support message');
                  setIsChatOpen(true);
                  setUserClosed(false);
                  sessionStorage.setItem('chat-open', 'true');
                  sessionStorage.removeItem('chat-closed');
                }
              }
              
              setMessages(prev => {
                const updatedMessages = [...prev, ...newSupportMessages];
                // Update localStorage immediately
                saveToCache({
                  sessionId: sessionIdRef.current,
                  userName: userName,
                  userEmail: userEmail,
                  messages: updatedMessages
                });
                return updatedMessages;
              });
            }
          }
        } catch (error) {
          console.error('Error polling for messages:', error);
        }
      }, 3000); // Poll every 3 seconds
    };

    // Start polling if we have a session and are in chat mode
    if (sessionId && chatStage === 'chat') {
      startPolling();
    }
    
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [sessionId, chatStage, userName, userEmail]);

  // Save to localStorage helper
  const saveToCache = (data) => {
    try {
      const cacheData = {
        ...data,
        timestamp: Date.now()
      };
      localStorage.setItem('spiral-chat-session', JSON.stringify(cacheData));
    } catch (error) {
      console.error('Failed to save chat to cache:', error);
    }
  };

  // FIXED: Only save chat data to cache when there are actual user messages
  useEffect(() => {
    if (sessionId && userName && userEmail && messages.some(msg => msg.sender === 'user')) {
      saveToCache({
        sessionId,
        userName,
        userEmail,
        messages
      });
    }
  }, [sessionId, userName, userEmail, messages]);

  const handleChatToggle = () => {
    if (isChatOpen) {
      setChatClosing(true);
      setIsChatOpen(false);
      setUserClosed(true);
      sessionStorage.setItem('chat-closed', 'true');
      sessionStorage.removeItem('chat-open');
      setTimeout(() => {
        setChatClosing(false);
      }, 300);
    } else {
      setIsChatOpen(true);
      setUserClosed(false);
      sessionStorage.setItem('chat-open', 'true');
      sessionStorage.removeItem('chat-closed');
    }
  };

  const handleNameSubmit = () => {
    if (userName.trim().length >= 2) {
      setChatStage('email');
    }
  };

  const handleEmailSubmit = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(userEmail.trim())) {
      setChatStage('chat');
      // Add welcome message
      const welcomeMessage = {
        id: `welcome-${Date.now()}`, 
        text: `Hi ${userName}! How can we help? Please feel free to ask your questions and provide as much detail as possible so we can assist you effectively.`, 
        sender: "support", 
        timestamp: new Date()
      };
      setMessages([welcomeMessage]);
      
      // FIXED: Don't save to cache until user sends a message
      // This prevents caching sessions where user never actually starts chatting
    }
  };

  const sendMessage = async () => {
    if (newMessage.trim() && !isLoading) {
      setIsLoading(true);
      
      const userMessage = {
        id: Date.now(),
        text: newMessage,
        sender: "user",
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, userMessage]);
      const messageToSend = newMessage;
      setNewMessage('');
      
      // Reset textarea height after clearing message
      if (messageInputRef.current) {
        messageInputRef.current.style.height = 'auto';
        messageInputRef.current.style.height = '40px';
      }
      
      try {
        const apiEndpoint = '/api/chat-send.php';
        
        const requestBody = {
          session_id: sessionId,
          clerk_user_id: clerkUser?.id || null,
          user_name: userName.trim(),
          user_email: userEmail.trim(),
          message: messageToSend
        };
        
        const response = await fetch(apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody)
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();
        
        if (result.success) {
          // Update session ID if this was the first message
          if (result.session_id && !sessionId) {
            setSessionId(result.session_id);
          }
          
          // Show auto-reply on first message
          const isFirstMessage = messages.filter(m => m.sender === 'user').length === 1;
          if (isFirstMessage) {
            setTimeout(() => {
              const autoReply = {
                id: Date.now() + 1,
                text: "We have received your message and someone will be with you shortly. Should you need to step away, we can get in touch through email.",
                sender: "support",
                timestamp: new Date()
              };
              setMessages(prev => [...prev, autoReply]);
            }, 500);
          }
        } else {
          throw new Error(result.error || 'Failed to send message');
        }
      } catch (error) {
        console.error('Error sending message:', error);
        
        setTimeout(() => {
          const errorMessage = {
            id: Date.now() + 2,
            text: "Uh oh, there was an error sending your message. Please try again or contact us via a different method.",
            sender: "support",
            timestamp: new Date()
          };
          setMessages(prev => [...prev, errorMessage]);
        }, 500);
      } finally {
        setIsLoading(false);
        // Refocus input after sending
        setTimeout(() => {
          messageInputRef.current?.focus();
        }, 100);
      }
    }
  };

  // Helper function to render message text with line breaks
  const formatMessageText = (text) => {
    // Just handle line breaks - let Slack handle emojis
    return text.split('\n').map((line, index, array) => (
      <React.Fragment key={index}>
        {line}
        {index < array.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  return {
    isChatOpen,
    chatClosing,
    chatStage,
    setChatStage,
    userName,
    setUserName,
    userEmail,
    setUserEmail,
    messages,
    newMessage,
    setNewMessage,
    isLoading,
    messageInputRef,
    messagesContainerRef,
    handleChatToggle,
    handleNameSubmit,
    handleEmailSubmit,
    sendMessage,
    formatMessageText
  };
};

// Enhanced Chat Widget Component
export const ChatWidget = ({ 
  theme, 
  isDarkMode,
  serverStatus,
  clerkUser
}) => {
  const {
    isChatOpen,
    chatClosing,
    chatStage,
    setChatStage,
    userName,
    setUserName,
    userEmail,
    setUserEmail,
    messages,
    newMessage,
    setNewMessage,
    isLoading,
    messageInputRef,
    messagesContainerRef,
    handleChatToggle,
    handleNameSubmit,
    handleEmailSubmit,
    sendMessage,
    formatMessageText
  } = useImprovedChat(clerkUser);

  return (
    <div className="fixed bottom-6 right-0 z-50">
      <button
        onClick={handleChatToggle}
        className={`absolute bottom-0 right-0 w-12 h-12 ${isDarkMode ? 'bg-slate-800/60 hover:bg-slate-800/80 border-slate-700/50' : 'bg-gray-100/60 hover:bg-gray-100/80 border-gray-300'} backdrop-blur-sm border-t border-l border-b ${theme.text.muted} rounded-tl-lg rounded-bl-lg shadow-lg flex items-center justify-center z-10`}
      >
        {(isChatOpen || chatClosing) ? (
          <X size={24} className={chatClosing ? "spin-out-animation" : "spin-animation"} />
        ) : (
          <MessageSquare size={24} className="transform -scale-x-100" />
        )}
      </button>

      {(isChatOpen || chatClosing) && (
        <div className={`absolute bottom-16 right-6 w-80 h-96 ${theme.chatBg} backdrop-blur-sm border rounded-2xl shadow-2xl flex flex-col ${isChatOpen && !chatClosing ? 'chat-open' : 'chat-closing'}`}>
          {/* Header */}
          <div className={`flex items-center justify-between p-4 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-gray-200'}`}>
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center">
                <img 
                  src="/sc_logo_notext.svg"
                  alt="Spiral Creations" 
                  className="h-8 w-auto"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div 
                  className={`w-8 h-8 ${isDarkMode ? 'bg-white' : 'bg-slate-800'} rounded-lg`}
                  style={{ display: 'none', alignItems: 'center', justifyContent: 'center', flexDirection: 'row' }}
                >
                  <div className="w-4 h-4 bg-pink-600 rounded-sm"></div>
                </div>
              </div>
              <div>
                <h3 className={`${theme.text.primary} font-semibold text-sm font-sans`}>Live Chat</h3>
                <p className={`${theme.text.muted} text-xs font-sans`}>
                  {chatStage === 'chat' ? 'We\'re here to help!' : 'Let\'s get started!'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
<div style={{position:'relative',width:'20px',height:'20px',flexShrink:0}}>
                {serverStatus.isOnline && (<>
                  <div style={{position:'absolute',top:'6px',left:'6px',width:'8px',height:'8px',borderRadius:'50%',backgroundColor:'#4ade80',animation:'pulseRing 2s ease-out infinite',opacity:0}} />
                  <div style={{position:'absolute',top:'6px',left:'6px',width:'8px',height:'8px',borderRadius:'50%',backgroundColor:'#4ade80',animation:'pulseRing 2s ease-out infinite',animationDelay:'1s',opacity:0}} />
                </>)}
                <div style={{position:'absolute',top:'6px',left:'6px',width:'8px',height:'8px',borderRadius:'50%',backgroundColor:serverStatus.isOnline?'#4ade80':'#f87171'}} />
              </div>
              <span className={`text-xs ${theme.text.secondary} font-sans`}>{serverStatus.isOnline ? 'Online' : 'Offline'}</span>
            </div>
          </div>

          {/* Content Area */}
          <div 
            ref={messagesContainerRef}
            className="flex-1 p-4 overflow-y-auto space-y-3 custom-scrollbar"
          >
            {chatStage === 'name' && (
              <div className="space-y-4">
                <div className={`${theme.text.primary} text-sm font-sans`}>
                  👋 Welcome! What's your name?
                </div>
                <div className="space-y-2">
                  <div className={`text-xs ${theme.text.secondary} font-sans flex items-center`}>
                    <User size={14} className="mr-2" />
                    Name
                  </div>
                  <input
                    type="text"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleNameSubmit();
                      }
                    }}
                    placeholder="Enter your name..."
                    className={`w-full ${theme.messageInput} rounded-lg px-3 py-2 text-sm font-sans focus:outline-none focus:ring-2 focus:ring-pink-500/30 focus:border-pink-500`}
                    autoFocus
                  />
                  <button
                    onClick={handleNameSubmit}
                    disabled={userName.trim().length < 2}
                    className="w-full bg-pink-500 hover:bg-pink-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white py-2 rounded-lg text-sm font-sans transition-colors"
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {chatStage === 'email' && (
              <div className="space-y-4">
                <div className={`${theme.text.primary} text-sm font-sans`}>
                  📧 Hi {userName}! What's your email address?
                </div>
                <div className="space-y-2">
                  <div className={`text-xs ${theme.text.secondary} font-sans flex items-center`}>
                    <Mail size={14} className="mr-2" />
                    Email
                  </div>
                  <input
                    type="email"
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleEmailSubmit();
                      }
                    }}
                    placeholder="your.email@example.com"
                    className={`w-full ${theme.messageInput} rounded-lg px-3 py-2 text-sm font-sans focus:outline-none focus:ring-2 focus:ring-pink-500/30 focus:border-pink-500`}
                    autoFocus
                  />
                  <button
                    onClick={handleEmailSubmit}
                    disabled={!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail.trim())}
                    className="w-full bg-pink-500 hover:bg-pink-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white py-2 rounded-lg text-sm font-sans transition-colors"
                  >
                    Start Chat
                  </button>
                </div>
                <button
                  onClick={() => setChatStage('name')}
                  className={`text-xs ${theme.text.muted} hover:${theme.text.secondary} transition-colors font-sans underline`}
                >
                  ← Back to name
                </button>
              </div>
            )}

            {chatStage === 'chat' && (
              <>                
                {messages.map((message) => (
                  <div key={message.id} className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-xs px-3 py-2 rounded-lg text-sm font-sans ${
                      message.sender === 'user' 
                        ? theme.userMessage + ' text-white'
                        : theme.supportMessage
                    }`}>
                      {formatMessageText(message.text)}
                    </div>
                  </div>
                ))}
                
                {isLoading && (
                  <div className="flex justify-center">
                    <div className={`${theme.supportMessage} px-3 py-2 rounded-lg text-sm font-sans`}>
                      <div className="flex items-center space-x-2">
                        <div className="animate-pulse">●</div>
                        <div className="animate-pulse" style={{animationDelay: '0.2s'}}>●</div>
                        <div className="animate-pulse" style={{animationDelay: '0.4s'}}>●</div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Input Area - Only show when in chat mode */}
          {chatStage === 'chat' && (
            <div className={`p-4 border-t ${isDarkMode ? 'border-slate-700/50' : 'border-gray-200'}`}>
              <div className="flex space-x-2 items-end">
                <textarea
                  ref={messageInputRef}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder="Type your message..."
                  disabled={isLoading}
                  rows="1"
                  className={`flex-1 ${theme.messageInput} rounded-lg px-3 py-2 text-sm font-sans focus:outline-none focus:ring-2 focus:ring-pink-500/30 focus:border-pink-500 disabled:opacity-50 resize-none min-h-[40px] max-h-[120px]`}
                  style={{
                    overflowY: newMessage.split('\n').length > 2 ? 'auto' : 'hidden',
                    height: 'auto'
                  }}
                  onInput={(e) => {
                    // Auto-resize textarea based on content
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                  }}
                />
                <button
                  onClick={sendMessage}
                  disabled={isLoading || !newMessage.trim()}
                  className="bg-pink-500 hover:bg-pink-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex-shrink-0 min-h-[40px] px-3 flex items-center justify-center"
                >
                  <Send size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Metrics Panel Component
// Tooltip Icon Component
const TooltipIcon = ({ isDarkMode, theme, text, open, onToggle }) => {
  return (
    <div className="relative flex-shrink-0">
      <button
        onClick={e => { e.stopPropagation(); onToggle(); }}
        className={`flex items-center justify-center transition-colors ${open ? 'text-pink-400' : theme.text.muted}`}
      >
        <Info size={12} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={e => { e.stopPropagation(); onToggle(); }} />
          <div className={`absolute right-0 top-6 z-50 w-56 rounded-xl shadow-xl p-3 text-xs font-sans leading-relaxed ${
            isDarkMode ? 'bg-slate-800 border border-slate-700/50 text-gray-300' : 'bg-white border border-gray-200 text-gray-600'
          }`}>
            {text}
          </div>
        </>
      )}
    </div>
  );
};

export const SystemStatusPanel = ({ 
  theme, 
  isDarkMode,
  statusOpen, 
  statusClosing, 
  handleStatusToggle,
  uptimeData,
  services,
  servicesLoading
}) => {
  const [animatedHours, setAnimatedHours] = useState(uptimeData.hours);
  const [animatedMinutes, setAnimatedMinutes] = useState(uptimeData.minutes);
  const [uptimeTooltip, setUptimeTooltip] = useState(false);
  const [serviceTooltip, setServiceTooltip] = useState(false);
  const animRef = useRef({});

  useEffect(() => {
    const animateTo = (key, setter, target, max) => {
      const state = animRef.current;
      if (state[key]) cancelAnimationFrame(state[key]);
      const from = state[`${key}Val`] ?? target;
      // Always animate the short way round, never go negative
      let delta = target - from;
      if (delta > max / 2) delta -= max;
      if (delta < -max / 2) delta += max;
      const duration = 800;
      let start = null;
      function step(ts) {
        if (!start) start = ts;
        const t = Math.min((ts - start) / duration, 1);
        const eased = t < 0.5 ? 2*t*t : -1+(4-2*t)*t;
        const val = ((from + delta * eased) % max + max) % max;
        state[`${key}Val`] = val;
        setter(val);
        if (t < 1) state[key] = requestAnimationFrame(step);
        else state[`${key}Val`] = target;
      }
      state[key] = requestAnimationFrame(step);
    };
    animateTo('hours', setAnimatedHours, uptimeData.hours, 24);
    animateTo('minutes', setAnimatedMinutes, uptimeData.minutes, 60);
  }, [uptimeData.hours, uptimeData.minutes]);

  if (!statusOpen && !statusClosing) return null;

  return (
    <div className="fixed top-32 inset-x-0 flex justify-center px-6" style={{zIndex: 100}}>
      <div className={`metrics-panel w-96 ${theme.chatBg} backdrop-blur-sm border rounded-2xl shadow-2xl p-6 relative ${statusOpen && !statusClosing ? 'metrics-open' : 'metrics-closing'}`}>
        <button
          onClick={handleStatusToggle}
          className={`absolute top-3 right-3 w-8 h-8 flex items-center justify-center ${theme.text.muted} ${theme.text.link} transition-colors duration-300 rounded-full ${isDarkMode ? 'bg-slate-700/50 hover:bg-slate-600/50 border border-slate-700/50' : 'bg-gray-200/50 hover:bg-gray-300/50 border border-gray-300'} z-50`}
        >
          <X size={16} className="spin-animation" />
        </button>
        
        <div className="text-center mb-6">
          <h2 className={`text-lg font-bold ${theme.text.primary} mb-1 font-sans title-animate`}>System & Service Status</h2>
          <p className={`text-xs ${theme.text.muted} font-sans subtitle-animate`}>Real-time server & service monitoring</p>
          <div className="h-0.5 mx-auto mt-2 rounded-full underline-animate" style={{backgroundColor: '#eb008b'}}></div>
        </div>
        
        {/* Clock-style uptime display */}
        <div className="cursor-pointer" onClick={() => { setUptimeTooltip(o => !o); setServiceTooltip(false); }}>
        <div className="flex items-center gap-3 mb-3">
          <h3 className={`text-xs font-sans font-semibold uppercase tracking-widest ${theme.text.muted} flex-shrink-0`}>Server Uptime</h3>
          <div className={`flex-1 h-px ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-200'}`} />
          <TooltipIcon isDarkMode={isDarkMode} theme={theme} text="Live real-time view of how long the server has been running since it came online." open={uptimeTooltip} onToggle={() => { setUptimeTooltip(o => !o); setServiceTooltip(false); }} />
        </div>

        <div className="flex justify-center mb-4">
          <div className="relative" style={{width: '260px', height: '260px'}}>
            {/* Background panel behind clock */}
            <div className={`absolute inset-0 rounded-full ${theme.card} border backdrop-blur-sm`} style={{
              boxShadow: isDarkMode ? '0 4px 24px rgba(0,0,0,0.2)' : '0 4px 24px rgba(0,0,0,0.08)',
            }} />
            {(() => {
              const cx = 115, cy = 115;
              const trackColor = isDarkMode ? "rgba(71,85,105,0.3)" : "rgba(209,213,219,0.5)";
              const rings = [
                { r: 90, sw: 7, fsw: 3, color: '#eb008b', glow: '#eb008b60', value: uptimeData.loading ? 0 : animatedMinutes, max: 60 },
                { r: 64, sw: 7, fsw: 3, color: '#eb008b', glow: '#eb008b60', value: uptimeData.loading ? 0 : animatedHours,   max: 24 },
              ];

              return (
                <>
                  {/* Arcs rotate so 0 is at top */}
                  <svg className="absolute inset-0 w-full h-full" viewBox="0 0 230 230"
                    style={{transform: 'rotate(-90deg)'}}>
                    {rings.map(({ r, sw, fsw, color, glow, value, max }, i) => {
                      const circ = 2 * Math.PI * r;
                      const filled = Math.min(value / max, 1) * circ;
                      return (
                        <g key={i}>
                          <circle cx={cx} cy={cy} r={r} fill="none" stroke={trackColor} strokeWidth={sw} />
                          <circle cx={cx} cy={cy} r={r} fill="none"
                            stroke={color} strokeWidth={fsw} strokeLinecap="butt"
                            strokeDasharray={`${filled} ${circ}`}
                            style={{filter:`drop-shadow(0 0 3px ${glow})`}} />
                        </g>
                      );
                    })}
                  </svg>

                  {/* Tip dots and tick marks in normal (unrotated) coordinate space */}
                  <svg className="absolute inset-0 w-full h-full" viewBox="0 0 230 230">
                    {/* Circular text around edge */}
                    <defs>
                      <path id="circleText" d={`M ${cx} ${cy + 105} a 105 105 0 1 1 0 -210 a 105 105 0 1 1 0 210`} />
                    </defs>
                    <text fontSize="5" fontFamily="'Barlow Condensed', sans-serif"
                      fill="rgba(156,163,175,0.25)"
                      style={{animation: 'circleShimmer 3s ease-in-out infinite'}}
                      fontWeight="600">
                      <textPath href="#circleText" startOffset="0.658%" textLength="659.734" lengthAdjust="spacing">
                        {'SPIRAL CREATIONS · SPIRAL CREATIONS · SPIRAL CREATIONS · SPIRAL CREATIONS · SPIRAL CREATIONS · SPIRAL CREATIONS · '}
                      </textPath>
                    </text>
                    {/* Tick marks: hours=24 divisions, minutes=12 divisions (every 5 min) */}
                    {rings.map(({ r, sw, max }, i) => {
                      const ticks = i === 0 ? 60 : 24; // outer=minutes 60 ticks, inner=hours 24 ticks
                      const majorEvery = i === 0 ? 5 : 6; // major tick every 5min / every 6h
                      return Array.from({ length: ticks }, (_, t) => {
                        const angle = (t / ticks) * 2 * Math.PI - Math.PI / 2;
                        const isMajor = t % majorEvery === 0;
                        const inner = r - sw * (isMajor ? 0.85 : 0.5);
                        const outer = r + sw * (isMajor ? 0.85 : 0.5);
                        return (
                          <line key={`${i}-${t}`}
                            x1={cx + inner * Math.cos(angle)} y1={cy + inner * Math.sin(angle)}
                            x2={cx + outer * Math.cos(angle)} y2={cy + outer * Math.sin(angle)}
                            stroke={isDarkMode ? `rgba(255,255,255,${isMajor ? 0.25 : 0.1})` : `rgba(0,0,0,${isMajor ? 0.2 : 0.08})`}
                            strokeWidth={isMajor ? 1.5 : 0.8} strokeLinecap="round" />
                        );
                      });
                    })}
                    {/* Days bubble above centre — rounded rect that grows with number */}
                    {(() => {
                      const days = uptimeData.loading ? '…' : uptimeData.days;
                      const chars = String(days).length;
                      const w = Math.max(18, chars * 6 + 10);
                      const h = 14;
                      return (
                        <>
                          <rect
                            x={cx - w/2} y={cy - 38 - h/2}
                            width={w} height={h}
                            rx={4} ry={4}
                            fill={isDarkMode ? '#1e293b' : '#fff'}
                            stroke={isDarkMode ? "rgba(71,85,105,0.5)" : "rgba(209,213,219,0.7)"}
                            strokeWidth="1.5"
                          />
                          <text x={cx} y={cy - 38} textAnchor="middle" dy="0.35em"
                            fill="white"
                            fontSize="8" fontWeight="600" fontFamily="sans-serif">
                            {days}
                          </text>
                        </>
                      );
                    })()}
                    <text x={cx} y={cy - 23} textAnchor="middle" dominantBaseline="middle"
                      fill="white"
                      fontSize="5" fontFamily="sans-serif" style={{letterSpacing: '0.08em'}}>
                      DAYS
                    </text>

                    {/* End-of-arc circles — rendered after days bubble so they're on top. MINS first, HRS last so HRS is on top */}
                    {[...rings].reverse().map(({ r, color, value, max }, i) => {
                      const origIndex = rings.length - 1 - i;
                      const angle = (value / max) * 2 * Math.PI - Math.PI / 2;
                      const ex = cx + r * Math.cos(angle);
                      const ey = cy + r * Math.sin(angle);
                      const isZero = Math.round(((value % max) + max) % max) === 0;
                      const circleColor = isZero
                        ? (isDarkMode ? 'rgba(71,85,105,0.5)' : 'rgba(209,213,219,0.7)')
                        : color;
                      return (
                        <g key={`end-${origIndex}`} style={{transition: 'all 1s ease-in-out'}}>
                          <circle cx={ex} cy={ey} r="9" fill={isDarkMode ? '#1e293b' : '#fff'}
                            stroke={circleColor} strokeWidth="1.5" />
                          <text x={ex} y={ey} textAnchor="middle" dy="0.35em"
                            fill="white" fontSize="8" fontWeight="700" fontFamily="sans-serif">
                            {Math.round(((value % max) + max) % max)}
                          </text>
                          <text x={ex} y={ey + 15} textAnchor="middle" dominantBaseline="middle"
                            fill="white" fontSize="5" fontFamily="sans-serif"
                            style={{letterSpacing: '0.08em'}}>
                            {origIndex === 0 ? 'MIN' : 'HRS'}
                          </text>
                        </g>
                      );
                    })}

                    {/* Centre text */}
                    {(() => {
                      const uptimeStr = uptimeData.loading ? '…' : `${uptimeData.days}d ${uptimeData.hours}h ${uptimeData.minutes}m`;
                      const fontSize = uptimeStr.length <= 8 ? 16 : uptimeStr.length <= 11 ? 14 : uptimeStr.length <= 14 ? 12 : 10;
                      return (
                        <text x={cx} textAnchor="middle" fontFamily="sans-serif">
                          <tspan x={cx} y={cy} dy="0"
                            fill="#4ade80" fontSize={fontSize} fontWeight="700"
                            fontFamily="'Barlow Condensed', sans-serif"
                            dominantBaseline="middle"
                            style={{letterSpacing: '0.02em'}}>
                            {uptimeStr}
                          </tspan>
                          <tspan x={cx} dy="13"
                            fill={isDarkMode ? "rgba(156,163,175,0.5)" : "rgba(107,114,128,0.5)"}
                            fontSize="6" dominantBaseline="middle"
                            style={{letterSpacing: '0.06em', animation: 'uptimeTextShimmer 3s ease-in-out infinite'}}>
                            {uptimeData.loading ? '' : 'SERVER UPTIME'}
                          </tspan>
                        </text>
                      );
                    })()}

                    {/* Heartbeat line in empty space below */}
                    <foreignObject x={cx - 18} y={cy + 26} width="36" height="24">
                      <canvas
                        width="72" height="48"
                        style={{width:'36px', height:'24px'}}
                        ref={el => {
                          if (!el || el._hbInit) return;
                          el._hbInit = true;
                          const ctx = el.getContext('2d');
                          const pts = [[0,6],[3,6],[4.5,1],[6,11],[7.5,3],[9,9],[10.5,6],[18,6]];
                          const scale = 4;
                          const path = [];
                          for (let i = 0; i < pts.length - 1; i++) {
                            const [x0,y0] = pts[i], [x1,y1] = pts[i+1];
                            const dx = x1-x0, dy = y1-y0;
                            const steps = Math.ceil(Math.hypot(dx,dy)/0.1);
                            for (let s = 0; s < steps; s++) path.push([x0+dx*s/steps, y0+dy*s/steps]);
                          }
                          path.push(pts[pts.length-1]);
                          const total = path.length;
                          const trailSize = Math.floor(total * 0.18);
                          const baseColor = isDarkMode ? '#475569' : '#d1d5db';
                          let start = null;
                          const duration = 3000;
                          function draw(ts) {
                            if (!el.isConnected) return;
                            if (!start) start = ts;
                            const t = ((ts - start) % duration) / duration;
                            ctx.clearRect(0, 0, el.width, el.height);
                            ctx.beginPath();
                            ctx.strokeStyle = baseColor;
                            ctx.lineWidth = 4;
                            ctx.lineCap = 'round';
                            ctx.lineJoin = 'round';
                            pts.forEach(([x,y],i) => i===0 ? ctx.moveTo(x*scale, y*scale) : ctx.lineTo(x*scale, y*scale));
                            ctx.stroke();
                            const head = Math.floor(t * (total + trailSize));
                            const tail = Math.max(0, head - trailSize);
                            const drawEnd = Math.min(head, total - 1);
                            const drawStart = Math.min(tail, total - 1);
                            if (drawStart < drawEnd) {
                              for (let i = drawStart; i < drawEnd; i++) {
                                const progress = (i - tail) / trailSize;
                                let alpha = 1;
                                if (progress < 0.15) alpha = progress / 0.15;
                                const exitedFraction = Math.max(0, head - total) / trailSize;
                                alpha *= (1 - exitedFraction);
                                ctx.beginPath();
                                ctx.strokeStyle = `rgba(74,222,128,${Math.max(0, Math.min(1, alpha))})`;
                                ctx.lineWidth = 4;
                                ctx.lineCap = 'round';
                                ctx.lineJoin = 'round';
                                ctx.moveTo(path[i][0]*scale, path[i][1]*scale);
                                ctx.lineTo(path[i+1][0]*scale, path[i+1][1]*scale);
                                ctx.stroke();
                              }
                            }
                            requestAnimationFrame(draw);
                          }
                          requestAnimationFrame(draw);
                        }}
                      />
                    </foreignObject>

                  </svg>
                </>
              );
            })()}
          </div>
        </div>

        {/* Service monitors */}
        </div> {/* end uptime clickable wrapper */}
        <div className="pt-4 mt-2 cursor-pointer" onClick={() => { setServiceTooltip(o => !o); setUptimeTooltip(false); }}>
          <div className="flex items-center gap-3 mb-3">
            <h3 className={`text-xs font-sans font-semibold uppercase tracking-widest ${theme.text.muted} flex-shrink-0`}>Service Health</h3>
            <div className={`flex-1 h-px ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-200'}`} />
            <TooltipIcon isDarkMode={isDarkMode} theme={theme} text="Real-time status of our services monitored from multiple global locations. Green bars show uptime history over the last 30 days." open={serviceTooltip} onToggle={() => { setServiceTooltip(o => !o); setUptimeTooltip(false); }} />
          </div>
          {servicesLoading ? (
            <div className={`text-center py-4 ${theme.text.muted} font-sans text-sm`}>Loading monitors…</div>
          ) : services.length === 0 ? (
            <div className={`text-center py-4 ${theme.text.muted} font-sans text-sm`}>No monitors found</div>
          ) : (
            <div className="space-y-3">
              {services.map((service, index) => (
                <div key={service.name} className={`${theme.card} border rounded-lg p-3 counter-animate`} style={{animationDelay: `${index * 0.1}s`}}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Globe size={13} className={theme.text.muted} />
                      <span className={`text-sm font-sans font-medium ${theme.text.primary}`}>{service.name}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
<div style={{position:'relative',width:'20px',height:'20px',flexShrink:0}}>
                        {service.status === 'online' && (<>
                          <div style={{position:'absolute',top:'6px',left:'6px',width:'8px',height:'8px',borderRadius:'50%',backgroundColor:'#4ade80',animation:'pulseRing 2s ease-out infinite',opacity:0}} />
                          <div style={{position:'absolute',top:'6px',left:'6px',width:'8px',height:'8px',borderRadius:'50%',backgroundColor:'#4ade80',animation:'pulseRing 2s ease-out infinite',animationDelay:'1s',opacity:0}} />
                        </>)}
                        <div style={{position:'absolute',top:'6px',left:'6px',width:'8px',height:'8px',borderRadius:'50%',backgroundColor:service.status==='online'?'#4ade80':service.status==='offline'?'#f87171':'#fbbf24'}} />
                      </div>
                      <span className={`text-xs font-sans font-medium ${service.status === 'online' ? 'text-green-400' : service.status === 'offline' ? 'text-red-400' : 'text-yellow-400'}`}>
                        {service.status === 'online' ? 'Online' : service.status === 'offline' ? 'Offline' : service.status}
                      </span>
                      {service.uptime && <span className={`text-xs font-sans ${theme.text.muted}`}>· {service.uptime}</span>}
                    </div>
                  </div>
                  <svg width="100%" height="22" viewBox="0 0 300 22" preserveAspectRatio="none">
                    {Array.from({ length: 30 }, (_, i) => {
                      const dayStatus = service.bars?.[i] ?? 'unknown';
                      const fill = dayStatus === 'up' ? 'rgba(74,222,128,0.85)' :
                                   dayStatus === 'down' ? 'rgba(239,68,68,0.85)' :
                                   dayStatus === 'degraded' ? 'rgba(251,191,36,0.85)' :
                                   isDarkMode ? 'rgba(71,85,105,0.4)' : 'rgba(209,213,219,0.5)';
                      return <rect key={i} x={i * 10 + 1.5} y={0} width={7} height={22} rx={2} fill={fill} />;
                    })}
                  </svg>
                  <div className="flex justify-between mt-1">
                    <span className={`text-[9px] font-sans ${theme.text.muted}`}>30 days ago</span>
                    <span className={`text-[9px] font-sans ${theme.text.muted}`}>Today</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

// Services Panel kept as alias for backwards compatibility
export const ServicesPanel = () => null;
export const MetricsPanel = () => null;

// Scroll to Top Button
export const ScrollToTopButton = ({ showScrollTop, scrollToTop, theme, isDarkMode }) => {
  if (!showScrollTop) return null;

  return (
    <div className="fixed bottom-20 right-2 z-50">
      <button
        onClick={scrollToTop}
        className={`w-8 h-8 ${isDarkMode ? 'bg-slate-800/60 hover:bg-slate-800/80 border-slate-700/50' : 'bg-gray-100/60 hover:bg-gray-100/80 border-gray-300'} backdrop-blur-sm border ${theme.text.muted} rounded-full shadow-lg flex items-center justify-center transition-all duration-300 transform hover:scale-110`}
      >
        <ChevronUp size={16} />
      </button>
    </div>
  );
};

// Notification Toast
export const NotificationToast = ({ notification, setNotification, theme }) => {
  if (!notification.show) return null;

  return (
    <div className="fixed top-24 right-6 z-50 notification-enter">
      <div className={`${theme.card} backdrop-blur-sm border rounded-lg p-4 shadow-xl max-w-sm ${
        notification.type === 'success' ? 'border-green-500/50 bg-green-500/10' :
        notification.type === 'error' ? 'border-red-500/50 bg-red-500/10' :
        'border-blue-500/50 bg-blue-500/10'
      }`}>
        <div className="flex items-start space-x-3">
          <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
            notification.type === 'success' ? 'bg-green-500 text-white' :
            notification.type === 'error' ? 'bg-red-500 text-white' :
            'bg-blue-500 text-white'
          }`}>
            {notification.type === 'success' ? '✓' : 
             notification.type === 'error' ? '✕' : 'i'}
          </div>
          <div>
            <p className={`text-sm font-medium ${theme.text.primary} font-sans`}>
              {notification.type === 'success' ? 'Success!' :
               notification.type === 'error' ? 'Error' : 'Info'}
            </p>
            <p className={`text-sm ${theme.text.secondary} font-sans mt-1`}>
              {notification.message}
            </p>
          </div>
          <button
            onClick={() => setNotification({ show: false, message: '', type: '' })}
            className={`flex-shrink-0 ${theme.text.muted} hover:${theme.text.primary} transition-colors`}
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};